import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, Field, Button, Modal, inr } from "../components/UI";
import Typeahead from "../components/Typeahead";
import { Calculator, Sparkles, Copy, AlertTriangle, Clock, ShieldCheck, ShoppingCart, Zap, MessageCircle, Lightbulb } from "lucide-react";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};
import { toast } from "sonner";

const tierStyles = {
  green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
  red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
};

export default function PricingCalculator() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [product, setProduct] = useState("Poster");
  const [attrs, setAttrs] = useState({
    size: "A3", gsm: 90, quantity: 5000,
    printing_side: "single", lamination: "none", uv: "none", foiling: "none", packing: "standard",
  });
  const [deliveryOverride, setDeliveryOverride] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Order booking modal
  const [orderOpen, setOrderOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [orderForm, setOrderForm] = useState({ customer_id: "", tier: "L1", notes: "" });

  // WhatsApp share modal
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTier, setShareTier] = useState(null);
  const [sharePhone, setSharePhone] = useState("");

  // Auto-discount AI tier suggestion
  const [tierSuggestion, setTierSuggestion] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");

  useEffect(() => {
    api.get("/pricing/config").then((r) => setConfig(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const calculate = async () => {
    setLoading(true);
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const payload = {
          product,
          attributes: { ...attrs, gsm: Number(attrs.gsm), quantity: Number(attrs.quantity) },
          delivery_cost: deliveryOverride === "" ? null : Number(deliveryOverride),
        };
        const r = await api.post("/ai-pricing/calculate", payload);
        setResult(r.data);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        // Only retry on 5xx (server transient)
        const s = err?.response?.status;
        if (!(s >= 500 && s < 600)) break;
        await new Promise((res) => setTimeout(res, 1500 * (attempt + 1)));
      }
    }
    toast.error(formatApiError(lastErr) || "AI calculation failed — please retry");
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(() => { if (config) calculate(); }, 400);
    return () => clearTimeout(t);
  }, [product, attrs, deliveryOverride, config]); // eslint-disable-line

  const copyTier = (tier) => {
    const text = `Singh Offset Quote
${product} · ${attrs.size} · ${attrs.gsm} GSM · ${attrs.quantity} pcs
Rate (${tier.label}): ${inr(tier.selling_total)}
Per unit: ${inr(tier.selling_total / attrs.quantity)}
Payment: ${tier.payment_terms}`;
    navigator.clipboard.writeText(text);
    toast.success("Quote copied!");
  };

  const bookOrder = (tier) => {
    setOrderForm({ customer_id: customers[0]?.id || "", tier: tier.key, notes: "" });
    setTierSuggestion(null);
    setSelectedCustomerLabel("");
    setOrderOpen(true);
  };

  const fetchSuggestion = async (cid) => {
    if (!cid) { setTierSuggestion(null); return; }
    setSuggestionLoading(true);
    try {
      const r = await api.get(`/customers/${cid}/suggested-tier`);
      setTierSuggestion(r.data);
      // Auto-update tier in form
      setOrderForm((f) => ({ ...f, tier: r.data.suggested_tier }));
    } catch { setTierSuggestion(null); }
    finally { setSuggestionLoading(false); }
  };

  const shareWhatsApp = (tier) => {
    setShareTier(tier);
    setSharePhone("");
    setShareOpen(true);
  };

  const sendWhatsApp = () => {
    if (!shareTier) return;
    const text = `*Singh Offset Quotation*
━━━━━━━━━━━━━━━━━
Product: *${product}*
Size: ${attrs.size} · ${attrs.gsm} GSM
Quantity: *${Number(attrs.quantity).toLocaleString("en-IN")} pcs*
${attrs.lamination !== "none" ? `Lamination: ${attrs.lamination}\n` : ""}${attrs.uv !== "none" ? `UV: ${attrs.uv}\n` : ""}
*Rate (${shareTier.label}):*
${inr(shareTier.selling_total)} (₹${(shareTier.selling_total / attrs.quantity).toFixed(2)}/unit)

*Payment:* ${shareTier.payment_terms}

Validity: 7 days
Reply to confirm order. Thank you!`;
    const cleanPhone = sharePhone.replace(/\D/g, "");
    const phoneFmt = cleanPhone.startsWith("91") ? cleanPhone : (cleanPhone ? `91${cleanPhone}` : "");
    const url = phoneFmt
      ? `https://wa.me/${phoneFmt}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    setShareOpen(false);
    toast.success("Opening WhatsApp…");
  };

  const confirmOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.customer_id) { toast.error("Select customer"); return; }
    const tier = result.tiers.find((t) => t.key === orderForm.tier);
    try {
      const r = await api.post("/ai-pricing/book-order", {
        customer_id: orderForm.customer_id,
        product,
        attributes: { ...attrs, gsm: Number(attrs.gsm), quantity: Number(attrs.quantity) },
        quantity: Number(attrs.quantity),
        selected_tier: orderForm.tier,
        selling_total: tier.selling_total,
        notes: orderForm.notes,
      });
      toast.success(`Order ${r.data.order_no} booked!`);
      setOrderOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!config) return <div className="p-8 text-[#475569]">Loading pricing engine…</div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="ai-pricing-page">
      <PageHeader
        title="AI Pricing Calculator"
        subtitle="Select product + attributes → AI engine calculates cost → L1 / L2 / L3 with payment terms"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Inputs */}
        <div className="lg:col-span-4 card-flat p-5 space-y-4" data-testid="ai-inputs">
          <Field label="Product" required>
            <select data-testid="ai-product" value={product} onChange={(e) => setProduct(e.target.value)} className="input-base">
              {config.product_types.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Paper Size" required>
              <select data-testid="ai-size" value={attrs.size} onChange={(e) => setAttrs({...attrs, size: e.target.value})} className="input-base">
                {config.paper_sizes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="GSM" required>
              <select data-testid="ai-gsm" value={attrs.gsm} onChange={(e) => setAttrs({...attrs, gsm: e.target.value})} className="input-base">
                {config.common_gsm.map((g) => <option key={g} value={g}>{g} GSM</option>)}
              </select>
            </Field>
          </div>

          <Field label="Quantity" required>
            <input data-testid="ai-qty" type="number" min="1" value={attrs.quantity} onChange={(e) => setAttrs({...attrs, quantity: e.target.value})} className="input-base font-mono-num text-lg" />
          </Field>

          <Field label="Printing Side">
            <select data-testid="ai-side" value={attrs.printing_side} onChange={(e) => setAttrs({...attrs, printing_side: e.target.value})} className="input-base">
              <option value="single">Single Side</option>
              <option value="double">Double Side</option>
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Lamination">
              <select value={attrs.lamination} onChange={(e) => setAttrs({...attrs, lamination: e.target.value})} className="input-base">
                {config.attribute_options.lamination.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="UV">
              <select value={attrs.uv} onChange={(e) => setAttrs({...attrs, uv: e.target.value})} className="input-base">
                {config.attribute_options.uv.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Foiling">
              <select value={attrs.foiling} onChange={(e) => setAttrs({...attrs, foiling: e.target.value})} className="input-base">
                {config.attribute_options.foiling.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Packing">
              <select value={attrs.packing} onChange={(e) => setAttrs({...attrs, packing: e.target.value})} className="input-base">
                {config.attribute_options.packing.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Delivery Cost Override (₹)">
            <input data-testid="ai-delivery" type="number" step="0.01" value={deliveryOverride} onChange={(e) => setDeliveryOverride(e.target.value)} className="input-base" placeholder="Default from settings" />
          </Field>
        </div>

        {/* Results */}
        <div className="lg:col-span-8 space-y-4" data-testid="ai-results">
          {loading && <div className="card-flat p-4 text-[#475569] text-sm">Calculating…</div>}

          {result && (
            <>
              {/* Cost Breakdown */}
              <div className="card-flat p-5" data-testid="cost-breakdown">
                <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Calculator size={16} className="text-[#C1121F]"/> Cost Breakdown</h3>
                <div className="space-y-1.5 text-sm">
                  {result.breakdown.items.map((it, i) => (
                    <div key={i} className="flex justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
                      <div>
                        <div>{it.label}</div>
                        {it.detail && <div className="text-[10px] text-[#94A3B8]">{it.detail}</div>}
                      </div>
                      <span className="font-mono-num font-semibold">{inr(it.value)}</span>
                    </div>
                  ))}
                  {result.ai.adjustments.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-amber-200 bg-amber-50 -mx-5 px-5 py-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1 flex items-center gap-1">
                        <Sparkles size={11}/> AI Rules Applied · {result.ai.rule_count} active rules
                      </div>
                      {result.ai.adjustments.map((a, i) => (
                        <div key={i} className="flex justify-between text-xs py-0.5">
                          <div className="flex-1">
                            <span className="font-semibold">{a.name}</span>
                            <span className="text-[#475569]"> — {a.reason}</span>
                          </div>
                          <span className="font-mono-num font-semibold text-amber-700">{a.amount >= 0 ? "+" : ""}{inr(a.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t border-[#E5E7EB] font-semibold">
                    <span>Cost Price</span><span className="font-mono-num">{inr(result.cost_price)}</span>
                  </div>
                  <div className="flex justify-between text-[#475569]">
                    <span>+ Delivery Cost</span><span className="font-mono-num">{inr(result.delivery_cost)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold pt-1 border-t border-[#E5E7EB]">
                    <span>Landed Cost</span><span className="font-mono-num text-[#C1121F]">{inr(result.landed_cost)}</span>
                  </div>
                </div>
              </div>

              {/* Tier Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tier-cards">
                {result.tiers.map((tier) => {
                  const s = tierStyles[tier.color];
                  return (
                    <div key={tier.key} className={`card-flat p-4 ${s.border} border-l-4`} data-testid={`tier-${tier.key}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${s.bg} ${s.text}`}>{tier.label}</span>
                        {tier.uplifted && <span className="text-[9px] text-amber-700 font-semibold">⬆ AI uplifted</span>}
                      </div>
                      <div className="font-display font-bold text-2xl font-mono-num">{inr(tier.selling_total)}</div>
                      <div className="text-xs text-[#475569] mt-0.5">{inr(tier.selling_total / Number(attrs.quantity))}/unit · {tier.margin_pct}% margin</div>
                      <div className="text-xs mt-1 text-green-700">Profit: <span className="font-mono-num font-bold">{inr(tier.profit_total)}</span></div>
                      <div className={`mt-3 p-2 ${s.bg} rounded text-[11px] font-bold ${s.text} flex items-start gap-1.5`}>
                        {tier.color === "red" ? <AlertTriangle size={12} className="mt-0.5 flex-shrink-0"/> :
                         tier.color === "amber" ? <Clock size={12} className="mt-0.5 flex-shrink-0"/> :
                         <ShieldCheck size={12} className="mt-0.5 flex-shrink-0"/>}
                        {tier.payment_terms}
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => copyTier(tier)} data-testid={`copy-${tier.key}`} className="text-[10px] font-semibold text-[#475569] hover:text-[#C1121F] py-1.5 px-2 border border-[#E5E7EB] rounded flex items-center justify-center gap-1" title="Copy quote">
                          <Copy size={10}/>
                        </button>
                        <button onClick={() => shareWhatsApp(tier)} data-testid={`whatsapp-${tier.key}`} className="text-[10px] font-semibold text-white bg-green-600 hover:bg-green-700 py-1.5 px-2 rounded flex items-center justify-center gap-1" title="Share on WhatsApp">
                          <MessageCircle size={10}/>
                        </button>
                        <button onClick={() => bookOrder(tier)} data-testid={`book-${tier.key}`} className="flex-1 text-[10px] font-semibold py-1.5 bg-[#C1121F] text-white rounded hover:bg-[#A00F19] flex items-center justify-center gap-1">
                          <ShoppingCart size={10}/> Book
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Meta */}
              <div className="text-[11px] text-[#94A3B8] flex flex-wrap gap-3 px-1">
                <span>Sheets: <b className="font-mono-num">{result.breakdown.meta.sheets_needed} + {result.breakdown.meta.wastage_sheets} wastage</b></span>
                <span>Impressions: <b className="font-mono-num">{result.breakdown.meta.impressions}</b></span>
                <span>Paper: <b className="font-mono-num">{result.breakdown.meta.paper_kg} kg</b></span>
                {user?.role === "admin" && <span>· <a href="/raw-costs" className="text-[#C1121F] font-semibold hover:underline">Edit Raw Costs</a> · <a href="/ai-instructions" className="text-[#C1121F] font-semibold hover:underline">AI Rules</a></span>}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal open={orderOpen} onClose={() => setOrderOpen(false)} title={`Book Order — ${orderForm.tier}`}>
        <form onSubmit={confirmOrder} className="space-y-3">
          <Field label="Customer" required>
            <Typeahead
              loadOptions={searchCustomersAsync}
              value={orderForm.customer_id}
              selectedLabel={selectedCustomerLabel}
              onChange={(id, opt) => {
                setOrderForm({ ...orderForm, customer_id: id });
                setSelectedCustomerLabel(opt?.name || "");
                fetchSuggestion(id);
              }}
              placeholder="Type to search customer…"
              required
              testid="book-customer"
              renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
            />
          </Field>

          {suggestionLoading && (
            <div className="text-xs text-[#475569] flex items-center gap-1.5">
              <Sparkles size={11} className="animate-pulse text-[#C1121F]"/> AI checking customer profile…
            </div>
          )}

          {tierSuggestion && (
            <div className="p-3 bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded" data-testid="tier-suggestion">
              <div className="flex items-center gap-2 mb-1">
                <Lightbulb size={14} className="text-[#C1121F]"/>
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#C1121F]">AI Suggestion</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${tierSuggestion.suggested_tier === "L1" ? "bg-green-100 text-green-700" : tierSuggestion.suggested_tier === "L2" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                  Recommend {tierSuggestion.suggested_tier}
                </span>
                <span className="text-[10px] text-[#94A3B8] ml-auto">{tierSuggestion.confidence} confidence</span>
              </div>
              <ul className="text-[11px] text-[#0F172A] space-y-0.5 ml-1">
                {tierSuggestion.reasons.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
              <div className="text-[10px] text-[#94A3B8] mt-1.5 flex flex-wrap gap-2">
                <span>LTV: <b className="font-mono-num">{inr(tierSuggestion.metrics.total_spent)}</b></span>
                <span>· Credit: <b className="font-mono-num">{tierSuggestion.metrics.credit_score}</b></span>
                {tierSuggestion.metrics.overdue_amount > 0 && <span className="text-red-600">· Overdue: <b className="font-mono-num">{inr(tierSuggestion.metrics.overdue_amount)}</b></span>}
              </div>
            </div>
          )}

          <Field label="Tier">
            <div className="flex gap-2">
              {["L1", "L2", "L3"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`tier-select-${t}`}
                  onClick={() => setOrderForm({ ...orderForm, tier: t })}
                  className={`flex-1 py-2 text-sm font-bold rounded border-2 ${orderForm.tier === t ? "bg-[#C1121F] text-white border-[#C1121F]" : "bg-white text-[#475569] border-[#E5E7EB] hover:border-[#C1121F]"}`}
                >
                  {t}
                  {tierSuggestion?.suggested_tier === t && <span className="ml-1 text-[10px]">⭐</span>}
                </button>
              ))}
            </div>
          </Field>

          {result && (() => {
            const t = result.tiers.find((x) => x.key === orderForm.tier);
            return (
              <div className="p-3 bg-[#F8F9FA] rounded text-xs space-y-1">
                <div><b>{product}</b> · {attrs.size} · {attrs.gsm} GSM</div>
                <div>Qty: <b className="font-mono-num">{attrs.quantity}</b></div>
                <div>Selling: <b className="font-mono-num text-[#C1121F]">{inr(t.selling_total)}</b></div>
                <div className="text-amber-700 font-semibold">⚠ {t.payment_terms}</div>
              </div>
            );
          })()}
          <Field label="Notes">
            <textarea value={orderForm.notes} onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOrderOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="confirm-book-btn"><Zap size={14}/> Book Order</Button>
          </div>
        </form>
      </Modal>

      <Modal open={shareOpen} onClose={() => setShareOpen(false)} title="Share Quote via WhatsApp">
        <div className="space-y-3">
          <div className="p-3 bg-green-50 border border-green-200 rounded text-xs">
            <div className="font-semibold text-green-800 mb-1">{shareTier?.label}</div>
            <div className="text-[#0F172A]">Rate: <b>{shareTier && inr(shareTier.selling_total)}</b> for {Number(attrs.quantity).toLocaleString("en-IN")} pcs</div>
            <div className="text-amber-700 mt-1">{shareTier?.payment_terms}</div>
          </div>
          <Field label="Customer Phone (optional, India)">
            <input
              data-testid="share-phone-input"
              type="tel"
              value={sharePhone}
              onChange={(e) => setSharePhone(e.target.value)}
              className="input-base"
              placeholder="9876543210 (leave empty to pick contact in WhatsApp)"
            />
          </Field>
          <div className="text-[11px] text-[#94A3B8]">
            Leave phone empty to pick contact yourself in WhatsApp. Or paste 10-digit number directly.
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button onClick={sendWhatsApp} data-testid="send-whatsapp-btn" className="bg-green-600 hover:bg-green-700">
              <MessageCircle size={14}/> Open WhatsApp
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
